/**
 * Field Provenance Schema
 *
 * Discriminated union schema for the `field_provenance` JSONB column on
 * entity tables. Each field's provenance entry tracks where the data came
 * from, with source-specific metadata.
 *
 * The union discriminates on the `source` field — no Effect-specific `_tag`
 * is stored in the database. This keeps the JSONB clean and framework-agnostic.
 */
import { Schema } from "effect"

// =============================================================================
// Shared Schema Components
// =============================================================================

/**
 * A reference cited by an Exa Webset enrichment
 */
export const ProvenanceReference = Schema.Struct({
  title: Schema.NullOr(Schema.String),
  url: Schema.String,
  snippet: Schema.optional(Schema.String)
})
export type ProvenanceReference = Schema.Schema.Type<typeof ProvenanceReference>

// =============================================================================
// Source-Specific Provenance Variants
// =============================================================================

/**
 * Exa Webset sources (company or people)
 */
export class WebsetProvenance extends Schema.Class<WebsetProvenance>("WebsetProvenance")({
  source: Schema.Literal("exa_company", "exa_people"),
  sourceUrl: Schema.NullOr(Schema.String),
  references: Schema.Array(ProvenanceReference),
  websetItemId: Schema.String,
  enrichmentId: Schema.String,
  reasoning: Schema.NullOr(Schema.String),
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Government registry verification (ABR, ASIC, FCA, etc.)
 */
export class GovernmentRegistryProvenance extends Schema.Class<GovernmentRegistryProvenance>("GovernmentRegistryProvenance")({
  source: Schema.Literal("government_registry"),
  registryName: Schema.String,
  lookupUrl: Schema.String,
  verifiedAt: Schema.DateTimeUtc,
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Underwriter self-reported via claimed profile
 */
export class ClaimedProvenance extends Schema.Class<ClaimedProvenance>("ClaimedProvenance")({
  source: Schema.Literal("claimed"),
  claimedByUserId: Schema.String,
  claimedByOrgId: Schema.optional(Schema.String),
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Admin manual override
 */
export class ManualProvenance extends Schema.Class<ManualProvenance>("ManualProvenance")({
  source: Schema.Literal("manual"),
  editedByUserId: Schema.String,
  reason: Schema.optional(Schema.String),
  // The agent's cited evidence URLs, carried through when an admin ACCEPTS an
  // agent proposal — so the verified badge keeps the sources that justified it,
  // not just "an admin". Empty for a standalone admin verify (no citation).
  // `reference` is the primary (first) URL, kept for back-compat; `sourceUrls`
  // is the full list the badge renders.
  reference: Schema.optional(Schema.NullOr(Schema.String)),
  sourceUrls: Schema.optional(Schema.Array(Schema.String)),
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Broker community report
 */
export class BrokerReportProvenance extends Schema.Class<BrokerReportProvenance>("BrokerReportProvenance")({
  source: Schema.Literal("broker_report"),
  reportedByUserId: Schema.String,
  reportedByOrgId: Schema.optional(Schema.String),
  sourceUrl: Schema.optional(Schema.String),
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Bulk import or legacy migration
 */
export class ImportProvenance extends Schema.Class<ImportProvenance>("ImportProvenance")({
  source: Schema.Literal("csv_import", "legacy_migration"),
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Web search research provenance (Tavily + LLM pipeline)
 */
export class WebSearchProvenance extends Schema.Class<WebSearchProvenance>("WebSearchProvenance")({
  source: Schema.Literal("web_search"),
  searchQuery: Schema.String,
  sourceUrls: Schema.Array(Schema.String),
  confidence: Schema.Number,
  reasoning: Schema.NullOr(Schema.String),
  enrichmentJobId: Schema.String,
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Document extraction provenance (insurer document intelligence pipeline)
 */
export class DocumentExtractionProvenance extends Schema.Class<DocumentExtractionProvenance>("DocumentExtractionProvenance")({
  source: Schema.Literal("document_extraction"),
  documentId: Schema.String,
  documentUrl: Schema.String,
  documentType: Schema.String,
  confidence: Schema.Number,
  reasoning: Schema.NullOr(Schema.String),
  extractedAt: Schema.DateTimeUtc
}) {}

/**
 * Super-admin enrichment agent provenance.
 *
 * Written when a raw agent finding is applied directly (e.g. high-confidence
 * auto-apply, a later phase). When a super-admin explicitly approves a proposed
 * diff, the value is applied as `manual` instead — so the human's decision
 * outranks future automated syncs. `agent_research` itself sits between
 * document_extraction (47) and web_search (45).
 */
export class AgentResearchProvenance extends Schema.Class<AgentResearchProvenance>("AgentResearchProvenance")({
  source: Schema.Literal("agent_research"),
  // NOTE: kept as plain strings (not the branded RunId/ProposalId schemas) — importing
  // those here creates a circular import (TDZ "Cannot access … before initialization").
  agentRunId: Schema.String,
  proposalId: Schema.NullOr(Schema.String),
  sourceUrls: Schema.Array(Schema.String),
  documentId: Schema.NullOr(Schema.String),
  confidence: Schema.Number,
  reasoning: Schema.NullOr(Schema.String),
  extractedAt: Schema.DateTimeUtc
}) {}

// =============================================================================
// Union & Record Schemas
// =============================================================================

/**
 * Discriminated union of all provenance entry types.
 * Discriminated on the `source` field — no `_tag` stored in the database.
 */
export const FieldProvenanceEntry = Schema.Union(
  WebsetProvenance,
  WebSearchProvenance,
  DocumentExtractionProvenance,
  AgentResearchProvenance,
  GovernmentRegistryProvenance,
  ClaimedProvenance,
  ManualProvenance,
  BrokerReportProvenance,
  ImportProvenance
)
export type FieldProvenanceEntry = Schema.Schema.Type<typeof FieldProvenanceEntry>

/**
 * The `field_provenance` JSONB column shape: Record<fieldName, FieldProvenanceEntry>
 */
export const FieldProvenance = Schema.Record({
  key: Schema.String,
  value: FieldProvenanceEntry
})
export type FieldProvenance = Schema.Schema.Type<typeof FieldProvenance>
